import { useEffect, useState, useCallback } from "react"
import { getW9Forms, approveW9, rejectW9 } from "@/services/payout.service"
import { useToast } from "@/context/ToastContext"
import { useAuth } from "@/context/AuthContext"
import { Badge } from "@/components/common/Badge"
import { Button } from "@/components/common/Button"
import { Modal, FormGroup, Textarea } from "@/components/common/Modal"
import { EmptyState } from "@/components/common/EmptyState"
import { LoadingRow } from "@/components/common/Spinner"
import { RefreshCw, CheckCircle, XCircle, FileText } from "lucide-react"

export function W9FormsPage() {
  const { toast }    = useToast()
  const { user }     = useAuth()
  const [forms, setForms]   = useState<Record<string, unknown>[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter]   = useState("all")
  const [acting, setActing]   = useState<string | null>(null)
  const [rejectOpen, setRejectOpen]   = useState(false)
  const [rejectTarget, setRejectTarget] = useState<string | null>(null)
  const [rejectReason, setRejectReason] = useState("")

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getW9Forms(filter === "all" ? undefined : filter)
      setForms(data)
    } catch { toast("Failed to load W-9 forms", "error") }
    finally { setLoading(false) }
  }, [filter, toast])

  useEffect(() => { load() }, [load])

  const pendingCount = forms.filter(f => f.status === "pending_review").length

  const handleApprove = async (id: string) => {
    setActing(id)
    try {
      await approveW9(id, user?.id ?? "")
      toast("W-9 approved", "success")
      load()
    } catch { toast("Failed to approve W-9", "error") }
    finally { setActing(null) }
  }

  const handleReject = async () => {
    if (!rejectTarget || !rejectReason) { toast("Reason required", "error"); return }
    setActing(rejectTarget)
    try {
      await rejectW9(rejectTarget, user?.id ?? "", rejectReason)
      toast("W-9 rejected", "success")
      setRejectOpen(false); setRejectReason("")
      load()
    } catch { toast("Failed to reject W-9", "error") }
    finally { setActing(null) }
  }

  const TAX_LABELS: Record<string, string> = {
    individual: "Individual/Sole Proprietor",
    c_corp: "C Corporation", s_corp: "S Corporation",
    partnership: "Partnership", llc_single: "LLC (Single Member)",
    llc_multi: "LLC (Multi Member)", other: "Other",
  }

  return (
    <div className="space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Total Submissions",  value: forms.length,  color: "bg-zimrah-500" },
          { label: "Pending Review",     value: pendingCount,  color: "bg-amber-500"  },
          { label: "Approved",           value: forms.filter(f => f.status === "approved").length, color: "bg-green-500" },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className={`h-1 ${s.color}`} />
            <div className="p-5">
              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-1">{s.label}</p>
              <p className="text-3xl font-black text-gray-900">{loading ? "—" : s.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3">
        <select value={filter} onChange={e => setFilter(e.target.value)}
          className="px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-zimrah-500 transition-all">
          <option value="all">All Forms</option>
          <option value="pending_review">Pending Review</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="not_submitted">Not Submitted</option>
        </select>
        <Button variant="secondary" icon={<RefreshCw size={14} />} onClick={load}>Refresh</Button>
        {pendingCount > 0 && (
          <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-xl">
            <FileText size={14} className="text-amber-600" />
            <span className="text-xs font-bold text-amber-700">{pendingCount} W-9s need review</span>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                {["Member", "Legal Name", "Tax Classification", "EIN/SSN", "Tax Year", "Signed", "Status", "Actions"].map(h => (
                  <th key={h} className="px-6 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-gray-400">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? <LoadingRow cols={8} /> :
               forms.length === 0 ? (
                <tr><td colSpan={8}>
                  <EmptyState icon="📋" title="No W-9 forms" subtitle="Member W-9 submissions will appear here." />
                </td></tr>
               ) : forms.map(f => (
                <tr key={f.id as string} className="border-t border-gray-50 hover:bg-gray-50/50 transition-colors">
                  <td className="px-6 py-4">
                    <p className="font-bold text-sm text-gray-900">
                      {(f.users as Record<string, unknown>)?.full_name as string ?? "—"}
                    </p>
                    <p className="text-xs text-gray-400">
                      {(f.users as Record<string, unknown>)?.email as string}
                    </p>
                  </td>
                  <td className="px-6 py-4 text-sm font-semibold text-gray-700">{f.legal_name as string}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {TAX_LABELS[f.tax_classification as string] ?? f.tax_classification as string}
                  </td>
                  <td className="px-6 py-4 font-mono text-sm text-gray-600">
                    •••-••-{f.ein_or_ssn_last4 as string}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700 font-bold">{f.tax_year as number}</td>
                  <td className="px-6 py-4 text-xs text-gray-400">
                    {f.signed_at ? new Date(String(f.signed_at ?? "")).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-6 py-4">
                    {f.status === "approved"       ? <Badge variant="success" dot>Approved</Badge>  :
                     f.status === "pending_review" ? <Badge variant="warning" dot>Pending Review</Badge> :
                     f.status === "rejected"       ? <Badge variant="danger"  dot>Rejected</Badge>  :
                     <Badge variant="muted" dot>Not Submitted</Badge>}
                  </td>
                  <td className="px-6 py-4">
                    {f.status === "pending_review" && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="success" icon={<CheckCircle size={13} />}
                          loading={acting === f.id as string}
                          onClick={() => handleApprove(String(f.id ?? ""))}>
                          Approve
                        </Button>
                        <Button size="sm" variant="danger" icon={<XCircle size={13} />}
                          onClick={() => { setRejectTarget(String(f.id ?? "")); setRejectOpen(true) }}>
                          Reject
                        </Button>
                      </div>
                    )}
                    {f.status === "rejected" && f.rejection_reason && (
                      <p className="text-xs text-red-500 max-w-xs">{f.rejection_reason as string}</p>
                    )}
                  </td>
                </tr>
               ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Reject Modal */}
      <Modal open={rejectOpen} onClose={() => setRejectOpen(false)} title="Reject W-9 Form" icon="❌"
        footer={
          <>
            <Button variant="secondary" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button variant="danger" loading={!!acting} onClick={handleReject}>Reject W-9</Button>
          </>
        }
      >
        <FormGroup label="Rejection Reason" required>
          <Textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)}
            placeholder="Explain why this W-9 is being rejected so the member can resubmit correctly…" />
        </FormGroup>
        <div className="p-3 rounded-xl bg-amber-50 border border-amber-100">
          <p className="text-xs font-semibold text-amber-700">
            The member will be notified of the rejection and can resubmit a corrected form.
          </p>
        </div>
      </Modal>
    </div>
  )
}